<template>
  <div class="market-info">
    <div class="lside">
      <img
        :alt="market.market_full_name"
        :src="getImageFromProxy(`${imagesUrl}markets/banners/${market.market_name}.svg`)"
      >
    </div>

    <div class="rside">
      <div class="rside__top">
        <span class="badge">
          {{ t(`market_type.${market.market_type?.toLowerCase()}`) }}
        </span>
        <div class="socials">
          <a
            v-if="market.social_media?.instagram"
            :href="market.social_media?.instagram"
            aria-label="Instagram"
            class="btn socials__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-instagram-2" />
            </svg>
          </a>
          <a
            v-if="market.social_media?.twitter"
            :href="market.social_media?.twitter"
            aria-label="Twitter"
            class="btn socials__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-twitter" />
            </svg>
          </a>
          <a
            v-if="market.social_media?.telegram"
            :href="market.social_media?.telegram"
            aria-label="Telegram"
            class="btn socials__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-telegram" />
            </svg>
          </a>
          <a
            v-if="market.social_media?.vk"
            :href="market.social_media?.vk"
            aria-label="Steam"
            class="btn socials__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              class="vk"
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-vk" />
            </svg>
          </a>
          <a
            v-if="market.social_media?.twitch"
            :href="market.social_media?.twitch"
            aria-label="Twitch"
            class="btn socials__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-twitch" />
            </svg>
          </a>
        </div>
      </div>

      <div class="rside__mid">
        <p class="title">
          {{ market.market_full_name }}
          <a
            :href="market.website_url"
            class="title__link"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="20"
              width="20"
            >
              <use xlink:href="/icons/sprite.svg#i-arrow-right-top" />
            </svg>
          </a>
        </p>

        <p class="desc">
          {{ market.summary }}
        </p>
      </div>

      <div class="rside__bottom">
        <div class="bottom-card review">
          <a
            :aria-label="t('trustpilot_rating')"
            :href="market.trust_pilot_link"
            rel="noopener nofollow"
            target="_blank"
          />
          <span class="bottom-card__label">{{ t('trustpilot_rating') }}</span>
          <PagesMarketComponentsRating
            :rating="market.rating || 0"
          />
          <span class="bottom-card__desc">
            {{ t('reviews_count', { n: getNumberShorts(market.votes || 0) }) }}
          </span>
        </div>
        <div class="bottom-card">
          <span class="bottom-card__label">
            {{ t('average_discount') }}
          </span>
          <div class="f fs gap4">
            <svg
              height="16"
              width="16"
            >
              <use xlink:href="/icons/sprite.svg#i-cash" />
            </svg>
            <span class="f16 fw600 white">
              -{{ market.supply_statistic?.avg_discount || 0 }}%
            </span>
          </div>
          <span class="bottom-card__desc">
            {{ t('compared_to_steam') }}
          </span>
        </div>
        <div class="bottom-card">
          <span class="bottom-card__label">
            {{ t('market_fee') }}
          </span>
          <div class="f fs gap4">
            <svg
              height="16"
              width="16"
            >
              <use xlink:href="/icons/sprite.svg#i-cash" />
            </svg>
            <span class="f16 fw600 white">
              {{ market.seller_fee || 0 }}%
            </span>
          </div>
          <span class="bottom-card__desc">
            {{ t('below_average') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { MarketFullInfo } from '~/graphql/generated/schema-types';

defineProps<{
  market: MarketFullInfo
}>();

const { t } = useI18n();

const config = useRuntimeConfig();
const imagesUrl = config?.public?.imagesUrl || '';
</script>

<style lang="scss" scoped>
.market-info {
  display: flex;
  gap: 32px;
  align-items: flex-start;

  .lside {
    width: 384px;
    height: 384px;
    padding: 50px;
    flex-shrink: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--surfaces-low-container);
    border-radius: 12px;

    &:after {
      content: '';
      position: absolute;
      top: 10%;
      left: 10%;
      width: 80%;
      height: 80%;
      padding: 8px;
      background: radial-gradient(50% 50% at 50% 50%, color-mix(in srgb, var(--accent-on-surface) 40%, transparent) 0%, transparent 100%);
    }

    img {
      width: 100%;
      height: 100%;
      object-position: center;
      object-fit: contain;
    }
  }

  .rside {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;

    &__top {
      display: flex;
      justify-content: space-between;
      gap: 16px;

      .badge {
        border-radius: 40px;
        background: var(--surfaces-highest-container);
        padding: 6px 8px;
      }

      .socials {
        display: flex;
        gap: 12px;

        &__link {
          width: 28px;
          height: 28px;
          padding: 0;
          color: #636E84;

          &:hover {
            color: var(--surfaces-on-surface);
          }

          svg {
            border-radius: 50%;
            overflow: hidden;
            width: 100%;
            height: 100%;

            &.vk {
              width: 24px;
              height: 24px;
            }
          }
        }
      }
    }

    &__mid {
      .title {
        color: var(--surfaces-on-surface);
        font-size: 28px;
        font-weight: 600;
        line-height: 36px; /* 128.571% */
        position: relative;
        width: max-content;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-bottom: 20px;

        &:hover {
          cursor: pointer;

          .title__link {
            color: var(--surfaces-on-surface);
          }
        }

        &__link {
          color: var(--surfaces-on-surface-variable);
          display: inline;

          &:after {
            content: '';
            position: absolute;
            inset: 0;
          }
        }
      }

      .desc {
        color: var(--surfaces-on-surface-variable);
        font-size: 16px;
        font-weight: 400;
        line-height: 32px; /* 200% */
        opacity: 0.8;
      }
    }

    &__bottom {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;

      .bottom-card {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px 16px;
        border-radius: 12px;
        background: var(--surfaces-low-container);
        color: var(--surfaces-on-surface-variable);
        font-size: 14px;

        &.review {
          position: relative;

          a:after {
            content: '';
            position: absolute;
            inset: 0;
          }
        }

        &:deep(.rating-stars .star) {
          line-height: 18px;
        }

        &__label {
          margin-bottom: 4px;
          font-weight: 600;
        }

        svg {
          position: relative;
          top: 1px;
        }
      }
    }
  }

  @media screen and (max-width: 1200px) {
    flex-direction: column;
    gap: 24px;

    .lside {
      width: 100%;
      height: 250px;
      padding: 20px;

      &:after {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  //@media screen and (max-width: 1200px) {
  //  display: none;
  //}
}
</style>
